import {useCallback, useState} from 'react';
import _ from 'underscore';

import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

export interface UseDataFrameTableSelectionOutput {
  selectedRowIds: Set<string>;
  onClickRow: (rowIndex: number, isShiftDown: boolean) => void;
  onClickSelectAll: (startRowIndex: number, endRowIndex: number) => void;
  // Clears memory of the last row clicked (which is used when shift-selecting)
  clearAnchorRow: () => void;
}

const dataFrameTableSelectionState = makeUseGlobalClientState(new Set<string>());

/**
 * Handles selection state for DataFrameTable
 * If every row in newSelectedRowIds is currently selected, every row in newSelectedRowIds will be deselected
 * If some row in newSelectedRowIds is not currently selected, every row in newSelectedRowIds will be selected
 * */
export default function useDataFrameTableSelectionState(
  clientStateId: string,
  rowIndexToRowId: {[rowIndex: number]: string}
): UseDataFrameTableSelectionOutput {
  const [selectedRowIds, setSelectedRowIds] =
    dataFrameTableSelectionState.useGlobalClientState(clientStateId);

  // Not using useGlobalClientState since we want to reset the anchor rows between mounts
  const [anchorRowIndex, setAnchorRowIndex] = useState<number | null>(null);

  const toggleRowIds = useCallback(
    (newSelectedRowIds: Array<string>, shouldToggleAnchorRow: boolean) => {
      const newSelectedRowIdsSet = new Set(selectedRowIds);
      // Toggle anchorRowId in the case of shift-selection because it gets pre-maturely toggled
      // when the user clicks on the first row of the selection range
      if (shouldToggleAnchorRow && anchorRowIndex != null) {
        const anchorRowId = rowIndexToRowId[anchorRowIndex];
        if (newSelectedRowIdsSet.has(anchorRowId)) {
          newSelectedRowIdsSet.delete(anchorRowId);
        } else {
          newSelectedRowIdsSet.add(anchorRowId);
        }
      }
      if (newSelectedRowIds.some((rowId) => !newSelectedRowIdsSet.has(rowId))) {
        setSelectedRowIds(new Set([...newSelectedRowIdsSet, ...newSelectedRowIds]));
      } else {
        setSelectedRowIds(
          new Set([...newSelectedRowIdsSet].filter((rowId) => !newSelectedRowIds.includes(rowId)))
        );
      }
    },
    [setSelectedRowIds, selectedRowIds, rowIndexToRowId, anchorRowIndex]
  );

  const getRowIdsInRange = useCallback(
    (startRowIndex: number, endRowIndex: number) =>
      _.range(startRowIndex, endRowIndex + 1).map((rowIndex) => rowIndexToRowId[rowIndex]),
    [rowIndexToRowId]
  );

  const onClickRow = useCallback(
    (rowIndex: number, isShiftDown: boolean) => {
      if (isShiftDown && anchorRowIndex != null) {
        const startRowIndex = Math.min(anchorRowIndex, rowIndex);
        const endRowIndex = Math.max(anchorRowIndex, rowIndex);

        const rowIdsInRange = getRowIdsInRange(startRowIndex, endRowIndex);
        toggleRowIds(rowIdsInRange, true);
      } else {
        toggleRowIds([rowIndexToRowId[rowIndex]], false);
      }
      setAnchorRowIndex(rowIndex);
    },
    [anchorRowIndex, rowIndexToRowId, toggleRowIds, getRowIdsInRange]
  );

  const onClickSelectAll = useCallback(
    (startRowIndex: number, endRowIndex: number) => {
      toggleRowIds(getRowIdsInRange(startRowIndex, endRowIndex), false);
    },
    [getRowIdsInRange, toggleRowIds]
  );

  const clearAnchorRow = useCallback(() => {
    setAnchorRowIndex(null);
  }, []);

  return {
    selectedRowIds,
    onClickRow,
    onClickSelectAll,
    clearAnchorRow,
  };
}
